// The four capacity numbers, computed in ONE place for every screen.
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────────
// Alex opened the app and read "960 minutes assigned against 480 available" on one screen while the
// calendar next to it showed something else. Two screens disagreeing about capacity is worse than
// either being wrong: a planner cannot tell which to believe, so they stop believing both.
//
// The cause was not one bad formula — it was FOUR of them. The week lane summed `minutes`, the project
// page read the server's conflict text, the month view had no total at all, and free capacity was
// inferred from the absence of a capacity row. This module is now the only implementation, and every
// screen renders what it returns. Two screens can no longer disagree because there is nothing left to
// disagree with.
//
// ── THE DEFINITIONS, ONCE ───────────────────────────────────────────────────────
//   assigned  = this viewer's own visible work + the anonymous total from projects they cannot see.
//               BOTH, always. Leaving out the invisible half is how a planner double-books somebody
//               who is already committed elsewhere.
//   capacity  = what the person can actually do that day: their daily default, or the availability
//               override, or 0 on a weekend or outside their active period. Straight from the server.
//   free      = max(0, capacity - assigned). Never negative — the overflow is `overload`, and a
//               negative "free" reads as a discount.
//   overload  = max(0, assigned - capacity). Never negative, for the mirror-image reason.
//
// free and overload are therefore never both non-zero, and `free - overload` is the signed slack.
import type { Schedule } from '../api/planning.ts';
import type { Ymd } from './dates.ts';

export interface DayCapacity {
  resourceId: number;
  day: Ymd;
  /** Minutes of work this viewer can see and name. */
  ownMinutes: number;
  /** Minutes on projects this viewer may not see. Counted, never attributed. */
  otherMinutes: number;
  assignedMinutes: number;
  capacityMinutes: number;
  freeMinutes: number;
  overloadMinutes: number;
  /** True when the person cannot work at all that day — weekend, leave, outside their active period. */
  nonWorking: boolean;
}

export type LoadLevel = 'empty' | 'light' | 'full' | 'over';

/**
 * The colour band. Three thresholds, because a planner reads colour before they read numbers.
 *
 *   green  — room left
 *   orange — full, or within a hair of it. NOT an error: a full day is the goal, not a problem.
 *   red    — more work than hours.
 *
 * `full` starts at 100% rather than at some 90% guess: "almost full" is a judgement this application
 * has no business making, and a planner who sees orange at 7 of 8 hours stops trusting orange.
 */
export function loadLevel(assigned: number, capacity: number): LoadLevel {
  if (assigned <= 0) return 'empty';
  if (capacity <= 0) return 'over';        // work on a day with no hours is always an overload
  if (assigned > capacity) return 'over';
  if (assigned === capacity) return 'full';
  return 'light';
}

/**
 * Index the schedule once, then answer per (resource, day) in constant time.
 *
 * Built as a Map rather than scanned per cell: a month view is 42 days by N lanes, and a linear
 * `find()` per cell made the calendar visibly stutter on a phone.
 */
export class CapacityIndex {
  private own = new Map<string, number>();
  private other = new Map<string, number>();
  private cap = new Map<string, number>();
  private seen = new Set<string>();

  constructor(schedule: Schedule | undefined) {
    if (!schedule) return;
    for (const i of schedule.items) {
      const k = key(i.resourceId, i.day);
      this.own.set(k, (this.own.get(k) ?? 0) + i.minutes);
      this.seen.add(k);
    }
    for (const o of schedule.otherCommitments) {
      const k = key(o.resourceId, o.day);
      this.other.set(k, (this.other.get(k) ?? 0) + o.minutes);
      this.seen.add(k);
    }
    for (const c of schedule.capacity) {
      const k = key(c.resourceId, c.day);
      this.cap.set(k, c.capacityMinutes);
      this.seen.add(k);
    }
  }

  at(resourceId: number, day: Ymd): DayCapacity {
    const k = key(resourceId, day);
    const ownMinutes = this.own.get(k) ?? 0;
    const otherMinutes = this.other.get(k) ?? 0;
    const assignedMinutes = ownMinutes + otherMinutes;
    // A MISSING CAPACITY ROW IS NOT ZERO CAPACITY — it is "the server did not send one", which before
    // migration 022 happened for every person with a free day. Treating absence as 0 painted every free
    // working day as a non-working one and made "8 u vrij" unreachable for exactly the people a planner
    // is hunting for. `nonWorking` is now only true when the server ACTUALLY said zero.
    const known = this.cap.has(k);
    const capacityMinutes = this.cap.get(k) ?? 0;
    return {
      resourceId, day, ownMinutes, otherMinutes, assignedMinutes, capacityMinutes,
      freeMinutes: Math.max(0, capacityMinutes - assignedMinutes),
      overloadMinutes: Math.max(0, assignedMinutes - capacityMinutes),
      nonWorking: known && capacityMinutes === 0,
    };
  }

  /** Totals across a set of days — what the month view puts under a date, and the day view in a header. */
  total(resourceIds: number[], days: Ymd[]): DayCapacity {
    const acc = {
      resourceId: -1, day: days[0] ?? '', ownMinutes: 0, otherMinutes: 0,
      assignedMinutes: 0, capacityMinutes: 0, freeMinutes: 0, overloadMinutes: 0, nonWorking: false,
    };
    for (const r of resourceIds) {
      for (const d of days) {
        const c = this.at(r, d);
        acc.ownMinutes += c.ownMinutes;
        acc.otherMinutes += c.otherMinutes;
        acc.assignedMinutes += c.assignedMinutes;
        acc.capacityMinutes += c.capacityMinutes;
      }
    }
    // DERIVED FROM THE TOTALS, NOT SUMMED FROM THE PARTS. Adding up per-person overloads would report
    // an overloaded team on a day when one person is 2 hours over and three others are idle — true of
    // the individual, false of the team, and the team number is the one a planner acts on.
    acc.freeMinutes = Math.max(0, acc.capacityMinutes - acc.assignedMinutes);
    acc.overloadMinutes = Math.max(0, acc.assignedMinutes - acc.capacityMinutes);
    return acc;
  }
}

const key = (resourceId: number, day: Ymd): string => `${resourceId}|${day}`;

/** "6 / 8 u" — the shape Alex asked for, with hours rather than minutes because people plan in hours. */
export const loadText = (c: DayCapacity): string =>
  `${fmt(c.assignedMinutes)} / ${fmt(c.capacityMinutes)} u`;

/** One decimal at most, comma as the separator, no trailing ",0". */
export const fmt = (minutes: number): string => {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1).replace('.', ',');
};
